import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  SafeAreaView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import Header from "../../components/Header";
import { useNavigation } from "@react-navigation/native";

const SignUpScreenPage: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<any>();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { signup, isLoading } = useAuth();

  // Theme-aware colors
  const themed = {
    background: theme === "dark" ? "#18181B" : "#FAFAFA",
    title: theme === "dark" ? "#FAFAFA" : "#2D3748",
    subtitle: theme === "dark" ? "#A1A1AA" : "#718096",
    inputBg: theme === "dark" ? "#27272A" : "#F7FAFC",
    inputBorder: theme === "dark" ? "#3A3A3D" : "#E2E8F0",
    inputText: theme === "dark" ? "#FAFAFA" : "#2D3748",
    iconColor: theme === "dark" ? "#A1A1AA" : "#718096",
    placeholderColor: theme === "dark" ? "#71717A" : "#718096",
    buttonBg: theme === "dark" ? "#FAFAFA" : "#272727",
    buttonText: theme === "dark" ? "#18181B" : "#FFFFFF",
    linkText: theme === "dark" ? "#93C5FD" : "#3B82F6",
    backText: theme === "dark" ? "#A1A1AA" : "#718096",
    errorBg: theme === "dark" ? "#431316" : "#FFF5F5",
    errorBorder: theme === "dark" ? "#7F1D1D" : "#FED7D7",
    errorText: theme === "dark" ? "#FCA5A5" : "#E53E3E",
  };

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email.toLowerCase());
  };

  const handleSignUp = async () => {
    // Basic validation
    if (!username.trim() || !email.trim() || !password.trim()) {
      setErrorMessage("All fields are required");
      return;
    }

    if (!validateEmail(email)) {
      setErrorMessage("Please enter a valid email address");
      return;
    }

    if (password.length < 6) {
      setErrorMessage("Password must be at least 6 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    try {
      setErrorMessage("");
      await signup(username, email, password);
      navigation.navigate("Home"); // Navigate to home after successful signup
    } catch (error: any) {
      console.error("Signup error:", error);
      setErrorMessage(
        error.message || "Failed to create account. Please try again.",
      );
    }
  };

  return (
    <SafeAreaView
      style={[styles.safeArea, { backgroundColor: themed.background }]}
      data-oid="g.dt-5n"
    >
      <Header data-oid="392nb7c" />

      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={[styles.container, { backgroundColor: themed.background }]}
        data-oid="psu07bu"
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          data-oid="9-ewtle"
        >
          <View style={styles.header} data-oid="edye6lq">
            <Text
              style={[styles.title, { color: themed.title }]}
              data-oid="cszb9vm"
            >
              Create an Account
            </Text>
            <Text
              style={[styles.subtitle, { color: themed.subtitle }]}
              data-oid="rxlris1"
            >
              Sign up to save opportunities and more
            </Text>
          </View>

          <View style={styles.formContainer} data-oid="fhwzg1v">
            {errorMessage ? (
              <View
                style={[
                  styles.errorContainer,
                  {
                    backgroundColor: themed.errorBg,
                    borderColor: themed.errorBorder,
                  },
                ]}
                data-oid="966o9_4"
              >
                <Ionicons
                  name="alert-circle-outline"
                  size={20}
                  color={themed.errorText}
                  data-oid="e35074r"
                />

                <Text
                  style={[styles.errorText, { color: themed.errorText }]}
                  data-oid="3797ug2"
                >
                  {errorMessage}
                </Text>
              </View>
            ) : null}

            <View
              style={[
                styles.inputContainer,
                {
                  backgroundColor: themed.inputBg,
                  borderColor: themed.inputBorder,
                },
              ]}
              data-oid="edeyft1"
            >
              <Ionicons
                name="person-outline"
                size={20}
                color={themed.iconColor}
                style={styles.inputIcon}
                data-oid="2on3-58"
              />

              <TextInput
                style={[styles.input, { color: themed.inputText }]}
                placeholder="Username"
                value={username}
                onChangeText={setUsername}
                autoCapitalize="none"
                autoCorrect={false}
                placeholderTextColor={themed.placeholderColor}
                data-oid="pvzemd0"
              />
            </View>

            <View
              style={[
                styles.inputContainer,
                {
                  backgroundColor: themed.inputBg,
                  borderColor: themed.inputBorder,
                },
              ]}
              data-oid="89_y98g"
            >
              <Ionicons
                name="mail-outline"
                size={20}
                color={themed.iconColor}
                style={styles.inputIcon}
                data-oid="ihkq:r8"
              />

              <TextInput
                style={[styles.input, { color: themed.inputText }]}
                placeholder="Email"
                value={email}
                onChangeText={setEmail}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
                placeholderTextColor={themed.placeholderColor}
                data-oid="xhnvoy2"
              />
            </View>

            <View
              style={[
                styles.inputContainer,
                {
                  backgroundColor: themed.inputBg,
                  borderColor: themed.inputBorder,
                },
              ]}
              data-oid="z3y:iq3"
            >
              <Ionicons
                name="lock-closed-outline"
                size={20}
                color={themed.iconColor}
                style={styles.inputIcon}
                data-oid=":d0a73j"
              />

              <TextInput
                style={[styles.input, { color: themed.inputText }]}
                placeholder="Password"
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                placeholderTextColor={themed.placeholderColor}
                data-oid="77gtgqi"
              />
            </View>

            <View
              style={[
                styles.inputContainer,
                {
                  backgroundColor: themed.inputBg,
                  borderColor: themed.inputBorder,
                },
              ]}
              data-oid="rz.j_35"
            >
              <Ionicons
                name="lock-closed-outline"
                size={20}
                color={themed.iconColor}
                style={styles.inputIcon}
                data-oid="qh9ffl5"
              />

              <TextInput
                style={[styles.input, { color: themed.inputText }]}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                secureTextEntry
                placeholderTextColor={themed.placeholderColor}
                data-oid="5dg47ht"
              />
            </View>

            <TouchableOpacity
              style={[
                styles.signupButton,
                { backgroundColor: themed.buttonBg },
              ]}
              onPress={handleSignUp}
              disabled={isLoading}
              data-oid="_y69ga2"
            >
              {isLoading ? (
                <ActivityIndicator
                  color={themed.buttonText}
                  size="small"
                  data-oid="7lih1g2"
                />
              ) : (
                <Text
                  style={[
                    styles.signupButtonText,
                    { color: themed.buttonText },
                  ]}
                  data-oid="3i7g6g-"
                >
                  Sign Up
                </Text>
              )}
            </TouchableOpacity>

            <View style={styles.loginContainer} data-oid="lsvl0xf">
              <Text
                style={[styles.loginText, { color: themed.subtitle }]}
                data-oid="dyygynp"
              >
                Already have an account?
              </Text>
              <TouchableOpacity
                onPress={() => navigation.navigate("Login")}
                data-oid="s4ywj7x"
              >
                <Text
                  style={[styles.loginLink, { color: themed.linkText }]}
                  data-oid="ojulg6i"
                >
                  Log In
                </Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.navigate("Home")}
              data-oid="jlk1e2-"
            >
              <Ionicons
                name="arrow-back"
                size={18}
                color={themed.backText}
                data-oid="34t33se"
              />

              <Text
                style={[styles.backButtonText, { color: themed.backText }]}
                data-oid="dd1fxqn"
              >
                Back to Opportunities
              </Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#FAFAFA",
  },
  container: {
    flex: 1,
    backgroundColor: "#FAFAFA",
  },
  scrollContent: {
    flexGrow: 1,
    padding: 24,
    paddingTop: 20,
    maxWidth: 600,
    width: "100%",
    alignSelf: "center",
  },
  header: {
    marginBottom: 40,
    alignItems: "center",
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#2D3748",
    marginBottom: 8,
    fontFamily: "Inter-Bold",
  },
  subtitle: {
    fontSize: 16,
    color: "#718096",
    fontFamily: "Inter-Regular",
    textAlign: "center",
  },
  formContainer: {
    width: "100%",
    maxWidth: 400,
    alignSelf: "center",
  },
  errorContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFF5F5",
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#FED7D7",
  },
  errorText: {
    color: "#E53E3E",
    marginLeft: 8,
    fontSize: 14,
    fontFamily: "Inter-Medium",
    flex: 1,
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F7FAFC",
    borderRadius: 12,
    paddingHorizontal: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  inputIcon: {
    marginRight: 12,
  },
  input: {
    flex: 1,
    height: 50,
    fontSize: 16,
    color: "#2D3748",
    fontFamily: "Inter-Regular",
  },
  signupButton: {
    backgroundColor: "#272727",
    borderRadius: 12,
    height: 50,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 10,
  },
  signupButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  loginContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 24,
    alignItems: "center",
  },
  loginText: {
    color: "#718096",
    fontSize: 14,
    fontFamily: "Inter-Regular",
  },
  loginLink: {
    color: "#3B82F6",
    fontSize: 14,
    fontWeight: "600",
    marginLeft: 4,
    fontFamily: "Inter-SemiBold",
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 40,
    paddingVertical: 12,
  },
  backButtonText: {
    color: "#718096",
    fontSize: 14,
    marginLeft: 6,
    fontFamily: "Inter-Medium",
  },
});

export default SignUpScreenPage;
